import { useState, useEffect } from 'react'
import { useGet, usePost } from '../utils/apiComponent/useApi'
import { apiClient } from '../utils/apiComponent/apiClient'
import { endpoints } from '../utils/endpoints'

export function useCourseManagement() {
  const [courses, setCourses] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [updateLoading, setUpdateLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)

  // Récupérer tous les cours - ne pas faire l'appel immédiatement, seulement quand nécessaire
  const { data: coursesData, loading: coursesLoading, error: coursesError, execute: fetchCourses } = useGet(
    null, // Ne pas faire l'appel par défaut
    { immediate: false }
  )

  // Créer un cours
  const { execute: createCourseApi, loading: createLoading } = usePost(endpoints.createCourse, { immediate: false })
  
  // Mettre à jour un cours: utilisation directe d'apiClient (URL dynamique)
  // Supprimer un cours: utilisation directe d'apiClient (URL dynamique)

  useEffect(() => {
    if (coursesData) {
      // L'API peut retourner { courses: [...] } ou directement [...]
      const coursesList = coursesData.courses || coursesData || []
      setCourses(coursesList)
    }
  }, [coursesData])

  useEffect(() => {
    setLoading(coursesLoading)
    setError(coursesError)
  }, [coursesLoading, coursesError])

  const createCourse = async (courseData) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await createCourseApi(courseData)
      
      if (response && typeof response === 'object' && response.id) {
        setCourses(prev => [...prev, response])
        return response
      } else if (response?.data) {
        setCourses(prev => [...prev, response.data])
        return response.data
      } else {
        throw new Error("Erreur lors de la création du cours")
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la création du cours"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateCourse = async (courseId, courseData) => {
    setLoading(true)
    setError(null)
    setUpdateLoading(true)
    
    try {
      // Vérifier si des fichiers sont présents (thumbnailFile ou bannerFile)
      const hasThumbnailFile = courseData.thumbnailFile instanceof File || courseData.thumbnailFile instanceof Blob
      const hasBannerFile = courseData.bannerFile instanceof File || courseData.bannerFile instanceof Blob
      const hasFiles = hasThumbnailFile || hasBannerFile
      
      let requestBody
      
      if (hasFiles) {
        // Utiliser FormData pour l'upload de fichiers
        const formData = new FormData()
        
        // Ajouter les fichiers si présents
        if (hasThumbnailFile) {
          formData.append('thumbnail', courseData.thumbnailFile)
        } else if (courseData.thumbnail !== undefined) {
          // Si thumbnail est fourni comme URL, l'ajouter
          formData.append('thumbnail', courseData.thumbnail)
        }
        
        if (hasBannerFile) {
          formData.append('banner', courseData.bannerFile)
        } else if (courseData.banner !== undefined) {
          // Si banner est fourni comme URL, l'ajouter
          formData.append('banner', courseData.banner)
        }
        
        // Ajouter tous les autres champs du cours s'ils sont définis
        Object.keys(courseData).forEach(key => {
          // Ignorer les propriétés de fichiers déjà traitées
          if (key !== 'thumbnailFile' && key !== 'bannerFile' && 
              !(key === 'thumbnail' && hasThumbnailFile) &&
              !(key === 'banner' && hasBannerFile)) {
            const value = courseData[key]
            if (value !== undefined && value !== null) {
              if (value instanceof File || value instanceof Blob) {
                formData.append(key, value)
              } else if (Array.isArray(value)) {
                // Pour les arrays, ajouter chaque élément
                value.forEach(item => {
                  formData.append(key, item)
                })
              } else if (typeof value === 'object') {
                // Pour les objets, les convertir en JSON string
                formData.append(key, JSON.stringify(value))
              } else {
                formData.append(key, value.toString())
              }
            }
          }
        })
        
        requestBody = formData
      } else {
        // Utiliser JSON pour les mises à jour sans fichiers
        const cleanedData = { ...courseData }
        // Retirer les propriétés de fichiers qui ne sont pas des fichiers
        delete cleanedData.thumbnailFile
        delete cleanedData.bannerFile
        
        requestBody = cleanedData
      }
      
      const response = await apiClient(endpoints.updateCourse(courseId), 'PATCH', { body: requestBody })
      
      if (response && typeof response === 'object' && response.id) {
        setCourses(prev => prev.map(course => 
          course.id === courseId ? response : course
        ))
        return response
      } else if (response?.data) {
        setCourses(prev => prev.map(course => 
          course.id === courseId ? response.data : course
        ))
        return response.data
      } else {
        throw new Error("Erreur lors de la mise à jour du cours")
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la mise à jour du cours"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setUpdateLoading(false)
    }
  }

  const updateModule = async (moduleId, moduleData) => {
    setLoading(true)
    setError(null)
    setUpdateLoading(true)
    try {
      const response = await apiClient(endpoints.updateModule(moduleId), 'PATCH', { body: moduleData })
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la mise à jour du module"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setUpdateLoading(false)
    }
  }

  const moveModuleUp = async (moduleId) => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient(endpoints.moveModuleUp(moduleId), 'POST')
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors du déplacement du module vers le haut"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const moveModuleDown = async (moduleId) => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient(endpoints.moveModuleDown(moduleId), 'POST')
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors du déplacement du module vers le bas"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const deleteModule = async (moduleId) => {
    setLoading(true)
    setError(null)
    setDeleteLoading(true)
    try {
      const response = await apiClient(endpoints.deleteModule(moduleId), 'DELETE')
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la suppression du module"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setDeleteLoading(false)
    }
  }

  const createLesson = async (lessonData) => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient(endpoints.createModuleLesson, 'POST', { body: lessonData })
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la création du chapitre "
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateLesson = async (lessonId, lessonData) => {
    setLoading(true)
    setError(null)
    setUpdateLoading(true)
    try {
      const response = await apiClient(endpoints.updateModuleLesson(lessonId), 'PATCH', { body: lessonData })
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la mise à jour du chapitre"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setUpdateLoading(false)
    }
  }

  const deleteLesson = async (lessonId) => {
    setLoading(true)
    setError(null)
    setDeleteLoading(true)
    try {
      const response = await apiClient(endpoints.deleteModuleLesson(lessonId), 'DELETE')
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la suppression du chapitre"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setDeleteLoading(false)
    }
  }

  const publishLesson = async (lessonId) => {
    setLoading(true)
    setError(null)
    setUpdateLoading(true)
    try {
      const response = await apiClient(endpoints.publishModuleLesson(lessonId), 'POST')
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la publication du chapitre"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setUpdateLoading(false)
    }
  }

  const unpublishLesson = async (lessonId) => {
    setLoading(true)
    setError(null)
    setUpdateLoading(true)
    try {
      const response = await apiClient(endpoints.unpublishModuleLesson(lessonId), 'POST')
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la dépublication du chapitre"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setUpdateLoading(false)
    }
  }

  const createBlock = async (blockData) => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiClient(endpoints.createModuleBlock, 'POST', { body: blockData })
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la création du bloc"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const updateBlock = async (blockId, blockData) => {
    setLoading(true)
    setError(null)
    setUpdateLoading(true)
    try {
      // LOG: Données reçues dans updateBlock
      console.log('[useCourseManagement] updateBlock - Données reçues:', {
        blockId,
        blockData: {
          ...blockData,
          image: blockData.image ? {
            name: blockData.image.name,
            type: blockData.image.type,
            size: blockData.image.size,
            isFile: blockData.image instanceof File,
            isBlob: blockData.image instanceof Blob
          } : undefined,
          imageFile: blockData.imageFile ? {
            name: blockData.imageFile.name,
            type: blockData.imageFile.type,
            size: blockData.imageFile.size,
            isFile: blockData.imageFile instanceof File,
            isBlob: blockData.imageFile instanceof Blob
          } : undefined
        }
      })
      
      // Vérifier si un fichier image est présent (pour les blocs IMAGE)
      const hasImageFile = blockData.imageFile instanceof File || blockData.imageFile instanceof Blob
      const hasImage = blockData.image instanceof File || blockData.image instanceof Blob
      const imageFile = blockData.image || blockData.imageFile // Priorité à "image"
      const hasFiles = hasImageFile || hasImage
      
      console.log('[useCourseManagement] updateBlock - Détection fichier:', {
        hasImageFile,
        hasImage,
        hasFiles,
        imageFile: imageFile ? {
          name: imageFile.name,
          type: imageFile.type,
          size: imageFile.size,
          isFile: imageFile instanceof File,
          isBlob: imageFile instanceof Blob
        } : null
      })
      
      let requestBody
      
      if (hasFiles && imageFile) {
        // Utiliser FormData pour l'upload de fichier image
        const formData = new FormData()
        
        // Ajouter le fichier image (le champ doit s'appeler "image" selon l'API)
        formData.append('image', imageFile)
        
        // Ajouter les autres champs
        if (blockData.title !== undefined) {
          formData.append('title', blockData.title)
        }
        if (blockData.description !== undefined) {
          formData.append('description', blockData.description)
        }
        if (blockData.type !== undefined) {
          formData.append('type', blockData.type)
        }
        if (blockData.order !== undefined) {
          formData.append('order', blockData.order.toString())
        }
        if (blockData.duration !== undefined) {
          formData.append('duration', blockData.duration.toString())
        }
        if (blockData.isPublished !== undefined) {
          formData.append('isPublished', blockData.isPublished.toString())
        }
        
        // Le content doit être une chaîne JSON selon la spécification
        if (blockData.content !== undefined) {
          if (typeof blockData.content === 'object') {
            // Convertir l'objet en chaîne JSON
            const contentString = JSON.stringify(blockData.content)
            formData.append('content', contentString)
            console.log('[useCourseManagement] updateBlock - Content JSON stringifié:', contentString)
          } else {
            formData.append('content', blockData.content.toString())
            console.log('[useCourseManagement] updateBlock - Content (string):', blockData.content)
          }
        }
        
        requestBody = formData
        
        // LOG: Contenu du FormData
        console.log('[useCourseManagement] updateBlock - FormData créé:', {
          hasImage: formData.has('image'),
          hasTitle: formData.has('title'),
          hasContent: formData.has('content'),
          title: formData.get('title'),
          content: formData.get('content'),
          type: formData.get('type'),
          duration: formData.get('duration')
        })
      } else {
        // Pas de fichier : utiliser JSON normal
        // Mais si c'est un bloc IMAGE, le content doit être une chaîne JSON
        if (blockData.content && typeof blockData.content === 'object') {
          blockData = {
            ...blockData,
            content: JSON.stringify(blockData.content)
          }
          console.log('[useCourseManagement] updateBlock - Content JSON stringifié (sans fichier):', blockData.content)
        }
        requestBody = blockData
        
        // LOG: Données JSON
        console.log('[useCourseManagement] updateBlock - Données JSON préparées:', requestBody)
      }
      
      console.log('[useCourseManagement] updateBlock - Envoi requête PATCH à:', endpoints.updateModuleBlock(blockId))
      
      const response = await apiClient(endpoints.updateModuleBlock(blockId), 'PATCH', { body: requestBody })
      
      // LOG: Réponse API
      console.log('[useCourseManagement] updateBlock - Réponse API complète:', response)
      console.log('[useCourseManagement] updateBlock - Réponse data:', response?.data)
      
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la mise à jour du bloc"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setUpdateLoading(false)
    }
  }

  const deleteBlock = async (blockId) => {
    setLoading(true)
    setError(null)
    setDeleteLoading(true)
    try {
      const response = await apiClient(endpoints.deleteModuleBlock(blockId), 'DELETE')
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la suppression du bloc"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setDeleteLoading(false)
    }
  }

  const reorderLessonBlocks = async (lessonId, blockIds) => {
    setLoading(true)
    setError(null)
    setUpdateLoading(true)
    try {
      const response = await apiClient(endpoints.reorderLessonBlocks(lessonId), 'PUT', { 
        body: { blockIds } 
      })
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors du réordonnancement des blocs"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setUpdateLoading(false)
    }
  }

  const publishCourse = async (courseId) => {
    setLoading(true)
    setError(null)
    setUpdateLoading(true)
    
    try {
      const response = await apiClient(endpoints.publishCourse(courseId), 'PATCH')
      setCourses(prev => prev.map(course => 
        course.id === courseId ? { ...course, status: 'PUBLISHED', isPublished: true } : course
      ))
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la publication du cours"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setUpdateLoading(false)
    }
  }

  const unpublishCourse = async (courseId) => {
    setLoading(true)
    setError(null)
    setUpdateLoading(true)
    
    try {
      const response = await apiClient(endpoints.unpublishCourse(courseId), 'PATCH')
      setCourses(prev => prev.map(course => 
        course.id === courseId ? { ...course, status: 'DRAFT', isPublished: false } : course
      ))
      return response?.data || response
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la dépublication du cours"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setUpdateLoading(false)
    }
  }

  const deleteCourse = async (courseId) => {
    setLoading(true)
    setError(null)
    setDeleteLoading(true)
    
    try {
      await apiClient(endpoints.deleteCourse(courseId), 'DELETE')
      
      setCourses(prev => prev.filter(course => course.id !== courseId))
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "Erreur lors de la suppression du cours"
      setError(errorMessage)
      throw err
    } finally {
      setLoading(false)
      setDeleteLoading(false)
    }
  }

  const loadCourses = () => {
    fetchCourses()
  }

  return {
    courses,
    loading,
    error,
    createLoading,
    updateLoading,
    deleteLoading,
    createCourse,
    updateCourse,
    publishCourse,
    unpublishCourse,
    updateModule,
    moveModuleUp,
    moveModuleDown,
    deleteModule,
    createLesson,
    updateLesson,
    deleteLesson,
    publishLesson,
    unpublishLesson,
    createBlock,
    updateBlock,
    deleteBlock,
    reorderLessonBlocks,
    deleteCourse,
    loadCourses
  }
}
